import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CheckCircle2, Cpu, BarChart3, Zap } from 'lucide-react';
import './OEELossAnalysis.css';

const OEELossAnalysis = () => {
    useEffect(() => {
        document.title = "OEE Monitoring & Loss Analysis | X-Max Manufacturing Excellence";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', 'Optimize production with X-Max real-time OEE Monitoring and Loss Analysis. Automated data capture via PLC/IoT for 100% accuracy.');
        }
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="oee-page">
            {/* Hero Section */}
            <section className="oee-hero">
                <div className="container oee-hero-container">
                    <div className="oee-hero-content">
                        <div className="sec-tag">CORE MODULES</div>
                        <h1>OEE & Loss Analysis: <span className="gradient-text">Driving Productivity</span></h1>
                        <p className="hero-desc">
                            X-Max provides comprehensive tools to measure Overall Equipment Effectiveness (OEE) and conduct in-depth Loss Analysis. This enables factories to move beyond guesswork, identify hidden inefficiencies, and systematically eliminate chronic losses.
                        </p>
                        <p className="hero-subtext">
                            We ensure <span className="highlight-text">accurate, real-time data</span> collection through seamless PLC and IoT sensor integration, eliminating manual entry and human error.
                        </p>
                        <Link to="/book-now" className="btn btn-primary btn-lg">
                            Book Demo <ChevronRight size={20} />
                        </Link>
                    </div>
                    <div className="oee-hero-visual">
                        <div className="visual-wrapper">
                            <img
                                src="./assets/modules/factory.png"
                                alt="Advanced Manufacturing Automation"
                                className="hero-img"
                            />
                            <div className="visual-overlay"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Redesigned Attractive Highlights Section */}
            <section className="oee-highlights">
                <div className="container">
                    <div className="highlights-grid">
                        {/* Point 1 */}
                        <div className="highlight-card">
                            <div className="card-step">01</div>
                            <div className="card-icon-box accent-orange">
                                <Cpu size={32} />
                            </div>
                            <div className="card-content">
                                <h3>Automated Data Capture</h3>
                                <p>Seamless PLC & IoT integration for 100% error-free data collection.</p>
                            </div>
                        </div>

                        {/* Point 2 */}
                        <div className="highlight-card">
                            <div className="card-step">02</div>
                            <div className="card-icon-box accent-blue">
                                <BarChart3 size={32} />
                            </div>
                            <div className="card-content">
                                <h3>Real-time Visualization</h3>
                                <p>Instant OEE calculations and live shop-floor dashboards.</p>
                            </div>
                        </div>

                        {/* Point 3 */}
                        <div className="highlight-card">
                            <div className="card-step">03</div>
                            <div className="card-icon-box accent-emerald">
                                <Zap size={32} />
                            </div>
                            <div className="card-content">
                                <h3>Actionable Insights</h3>
                                <p>Granular loss categorization to drive measurable improvements.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="oee-details">
                <div className="container">
                    <div className="details-layout">
                        <div className="details-text">
                            <p>
                                Our system automatically collects data directly from your machines via <strong>PLC and IoT sensors</strong>, feeding it into the OEE calculation engine without human intervention. This real-time data flow ensures that OEE metrics (Availability, Performance, Quality) are always up-to-date and highly accurate.
                            </p>
                            <p>
                                The platform then categorises identified losses against the 16 Big Losses framework, providing granular insights into downtime, speed, and quality issues. This structured approach facilitates root cause analysis and empowers your teams to implement effective corrective actions swiftly.
                            </p>
                        </div>
                        <div className="details-features">
                            <h2>Key Features</h2>
                            <ul className="feature-list">
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Six Big Loss categorization framework</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Automated data collection from machines</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Pareto analysis for prioritization</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Custom report generation</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Action plan tracking</span>
                                </li>
                            </ul>
                            <div className="details-cta">
                                <button className="link-btn">
                                    Request Features List <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="oee-footer-cta">
                <div className="container">
                    <div className="cta-card">
                        <h2>Ready to transform your production visibility?</h2>
                        <Link to="/book-now" className="btn btn-primary btn-lg">Book a Deep-Dive Demo</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OEELossAnalysis;
